/**
 * The one loading indicator of the application: the animated MyEquip mark.
 *
 * - `startup-mobile` / `startup-desktop`: full-screen start composition, shown
 *   under the static start screen of `index.html` while the session is read;
 * - `page`: a page (or its whole content) is loading;
 * - `section`: a block inside a page (card, panel, step of a form).
 *
 * In a page or section the mark only appears after a short delay, and leaves
 * the instant the content is ready: content is never held back for it.
 * Screen readers get the label at once ("Chargement" unless told otherwise).
 * Markup and styles are shared with the start screen: `core/brand-loader.ts`.
 */
import type { ReactNode } from 'react';
import {
  brandLoaderClass, DESKTOP_MIN_WIDTH, MARK_DOT, MARK_LOWER, MARK_UPPER, MARK_VIEWBOX,
  type BrandLoaderVariant,
} from '../core/brand-loader.js';
import { t } from '../core/i18n.js';

/** The animated mark alone, for a state that already carries its own text. */
export function BrandLoaderArt(): ReactNode {
  return (
    <span className="brand-loader__art" aria-hidden="true">
      <svg className="brand-loader__mark" viewBox={MARK_VIEWBOX} focusable="false">
        <path className="brand-loader__upper" d={MARK_UPPER} />
        <path className="brand-loader__lower" d={MARK_LOWER} />
        <rect className="brand-loader__halo" x={MARK_DOT.x} y={MARK_DOT.y} width={MARK_DOT.size} height={MARK_DOT.size} />
        <rect className="brand-loader__dot" x={MARK_DOT.x} y={MARK_DOT.y} width={MARK_DOT.size} height={MARK_DOT.size} />
      </svg>
      <span className="brand-loader__word" />
    </span>
  );
}

export function BrandLoader({ variant = 'page', label }: {
  variant?: BrandLoaderVariant | undefined;
  /** Spoken label; defaults to "Chargement". Never displayed. */
  label?: string | undefined;
}): ReactNode {
  return (
    <div className={brandLoaderClass(variant)} role="status">
      <BrandLoaderArt />
      <span className="brand-loader__label">{label ?? t('commun.chargement')}</span>
    </div>
  );
}

/** The animated mark inside a state that displays its own text (never announced). */
export function BrandLoaderMark(): ReactNode {
  return (
    <span className="brand-loader brand-loader--mark">
      <BrandLoaderArt />
    </span>
  );
}

/** Start composition matching the screen, as chosen by the static start screen. */
export function startupVariant(): BrandLoaderVariant {
  return window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH}px)`).matches
    ? 'startup-desktop'
    : 'startup-mobile';
}
